// A person's identity (docs/phase-3-messages.md § Identité d'une personne).
//
// The Ed25519 identity key is DERIVED from the genesis secret of my space, so every device of
// mine recomputes the same key and nothing has to be synchronised. It attests my devices to my
// contacts ("this device is William's") and gives the safety number both sides compare.

import { b64url, fromB64url, toHex } from '../core/ids';
import { verify, type DeviceRecord } from '../space/keys';

const enc = new TextEncoder();
// PKCS#8 wrapper of a raw 32-byte Ed25519 seed (RFC 8410).
const PKCS8_ED25519 = new Uint8Array([0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20]);

export type Identity = { pub: string; sign: CryptoKey };

export async function identityFrom(genesisSecret: string): Promise<Identity> {
  const base = await crypto.subtle.importKey('raw', fromB64url(genesisSecret), 'HKDF', false, ['deriveBits']);
  const seed = new Uint8Array(await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: enc.encode('space-v1'), info: enc.encode('identity-v1') }, base, 256));
  const pkcs8 = new Uint8Array([...PKCS8_ED25519, ...seed]);
  // Imported once extractable only to read the public half (JWK "x"), then for use non-extractable.
  const jwk = await crypto.subtle.exportKey('jwk', await crypto.subtle.importKey('pkcs8', pkcs8, { name: 'Ed25519' }, true, ['sign']));
  const sign = await crypto.subtle.importKey('pkcs8', pkcs8, { name: 'Ed25519' }, false, ['sign']);
  seed.fill(0);
  pkcs8.fill(0);
  return { pub: jwk.x!, sign };
}

const attestation = (person: string, d: DeviceRecord) => enc.encode(`device-of-v1\n${person}\n${d.id}\n${d.signPub}\n${d.dhPub}`);

/** The device record, signed as belonging to this person. */
export async function attest(identity: Identity, d: DeviceRecord): Promise<DeviceRecord> {
  const { id, name, signPub, dhPub } = d;
  const personSig = b64url(new Uint8Array(await crypto.subtle.sign({ name: 'Ed25519' }, identity.sign, attestation(identity.pub, d))));
  return { id, name, signPub, dhPub, person: identity.pub, personSig };
}

export async function checkAttestation(d: DeviceRecord): Promise<boolean> {
  return !!d.person && !!d.personSig && verify(d.person, d.personSig, attestation(d.person, d));
}

/** 12 groups of 5 digits, the same on both sides (order-independent), as in Signal. */
export async function safetyNumber(a: string, b: string): Promise<string> {
  const [x, y] = [a, b].sort();
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-512', new Uint8Array([...fromB64url(x), ...fromB64url(y)])));
  const groups: string[] = [];
  for (let i = 0; i < 12; i++) {
    const n = (hash[i * 3] << 16) | (hash[i * 3 + 1] << 8) | hash[i * 3 + 2];
    groups.push(String(n % 100_000).padStart(5, '0'));
  }
  return groups.join(' ');
}

/** Short, stable label of a person key for logs and tests. */
export function personTag(pub: string): string {
  return toHex(fromB64url(pub)).slice(0, 8);
}
